import React from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';

import {
  Container,
  Card,
  Row,
  Label,
} from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import {
  placeTypeOptions,
} from 'components/AddLocation/StepBasic/config';
import DataTableRevenue from 'components/App/DataTableRevenue';
import Pagination from 'components/Pagination';

import { themeColor } from 'constants/index';
import cn from 'utils/classnames';
import getTotalPage from 'utils/totalPage';

import DownloadIcon from 'assets/images/download.svg';
import SearchIcon from 'assets/images/search-icon.svg';

import styles from './ManagerRevenue.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý doanh thu',
    isActive: false,
    link: '',
  },
];

const DEFAULT_LIMIT = 10;
const MARKET_PAGE_LIMIT = 8;
// const ADMINISTRATOR_PAGE_LIMIT = 8;

const STAR_RATING_OPTIONS = [
  { label: '5 sao', value: 5 },
  { label: '4 sao', value: 4 },
  { label: '3 sao', value: 3 },
  { label: '2 sao', value: 2 },
  // { label: '1 sao', value: 1 },
];

// const STATUS_OPEN_CLOSE_OPTIONS = [
//   { label: 'Mở', value: 'true' },
//   { label: 'Đóng', value: 'false' },
// ];

const DEFAULT_SORT_BY = 'starRating';
const DEFAULT_SORT_ORDER = 'desc';

function ManagerRevenue(props) {
  const {
    isLoadingRevenueList,
    isLoadingExportRevenueList,
    revenueList,
    totalRevenue,
    marketList,
    marketTotal,
    isLoadingMarketList,
    landmarkOptions,
    isLoadingLandmarkList,
    // administratorsList,
    // administratorsTotal,
    // isLoadingGetAdministratorsList,

    actionGetRevenueList,
    actionExportRevenueList,
    actionGetMarketList,
    actionGetLandmarkList,
    actionClearLandmarkList,
    // actionGetAdministratorsList,
  } = props;

  const history = useHistory();
  const [marketPage, setMarketPage] = React.useState(1);
  const [marketSearchQuery, setMarketSearchQuery] = React.useState('');
  const [selectedMarketLabel, setSelectedMarketLabel] = React.useState('');
  // const [administratorPage, setAdministratorPage] = React.useState(1);
  // const [administratorSearchQuery, setAdministratorSearchQuery] =
  //   React.useState('');
  // const [selectedAdministratorLabel, setSelectedAdministratorLabel] =
  //   React.useState('');
  const [sortBy, setSortBy] = React.useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = React.useState(DEFAULT_SORT_ORDER);

  const hasMoreMarkets = marketList.length < marketTotal;
  // const hasMoreAdministrators = administratorsList.length < administratorsTotal;

  const marketOptions = React.useMemo(
    () =>
      marketList.map((item) => ({
        label: item.name,
        value: item._id,
      })),
    [marketList],
  );

  // const administratorOptions = React.useMemo(
  //   () =>
  //     administratorsList.map((item) => ({
  //       label: item.fullName,
  //       value: item._id,
  //     })),
  //   [administratorsList],
  // );

  const urlPage = React.useMemo(
    () => Number(new URLSearchParams(history.location.search).get('page') || 1),
    [history.location.search],
  );

  const validation = useFormik({
    initialValues: {
      q: '',
      market: '',
      placeType: '',
      landmark: '',
      starRating: '',
      status: '',
      isPublished: '',
      administrator: '',
    },
    onSubmit: () => {},
  });

  const filterValuesRef = React.useRef(validation.values);
  filterValuesRef.current = validation.values;

  const buildRevenueListPayload = React.useCallback(
    (overrides = {}) => {
      const payload = {
        sortField: sortBy,
        sortOrder,
        page: urlPage,
        limit: DEFAULT_LIMIT,
      };

      Object.entries(filterValuesRef.current).forEach(([key, value]) => {
        if (value !== '' && value != null) {
          payload[key] = value;
        }
      });

      return {
        ...payload,
        ...overrides,
      };
    },
    [sortBy, sortOrder, urlPage],
  );

  const fetchRevenueList = React.useCallback(
    (overrides = {}) => {
      actionGetRevenueList(buildRevenueListPayload(overrides));
    },
    [actionGetRevenueList, buildRevenueListPayload],
  );

  const buildRevenueExportPayload = React.useCallback(() => {
    const { page, limit, ...exportPayload } = buildRevenueListPayload();

    return exportPayload;
  }, [buildRevenueListPayload]);

  const onExportRevenueList = React.useCallback(() => {
    actionExportRevenueList(buildRevenueExportPayload());
  }, [actionExportRevenueList, buildRevenueExportPayload]);

  const onSearchSubmit = React.useCallback(
    (event) => {
      event.preventDefault();
      fetchRevenueList({ page: 1 });

      if (urlPage !== 1) {
        history.push('?page=1');
      }
    },
    [fetchRevenueList, history, urlPage],
  );

  const onSort = React.useCallback(
    (columnSortBy) => {
      const nextSortOrder =
        sortBy === columnSortBy && sortOrder === 'desc' ? 'asc' : 'desc';
      const resolvedSortOrder =
        sortBy === columnSortBy ? nextSortOrder : DEFAULT_SORT_ORDER;

      setSortBy(columnSortBy);
      setSortOrder(resolvedSortOrder);

      if (urlPage !== 1) {
        history.push('?page=1');
        return;
      }

      fetchRevenueList({
        sortField: columnSortBy,
        sortOrder: resolvedSortOrder,
        page: 1,
      });
    },
    [fetchRevenueList, history, sortBy, sortOrder, urlPage],
  );

  const onChangeFilterOption = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e.value, true);
    },
    [validation],
  );

  const marketSelectValue = React.useMemo(() => {
    const marketId = validation.values.market;

    if (!marketId) {
      return null;
    }

    const labelFromList = marketList.find(
      (item) => item._id === marketId,
    )?.name;

    return {
      label: selectedMarketLabel || labelFromList || '',
      value: marketId,
    };
  }, [validation.values.market, selectedMarketLabel, marketList]);

  const onMarketChange = React.useCallback(
    (e) => {
      validation.setFieldValue('market', e?.value || '', true);
      validation.setFieldValue('landmark', '', true);
      setSelectedMarketLabel(e?.label || '');

      if (!e?.value) {
        actionClearLandmarkList();
        return;
      }

      if (e?.value) {
        actionGetLandmarkList({
          market: e.value,
          marketName: e.label,
        });
        return;
      }

      actionClearLandmarkList();
    },
    [
      actionClearLandmarkList,
      actionGetLandmarkList,
      validation,
    ],
  );

  // const administratorSelectValue = React.useMemo(() => {
  //   const administratorId = validation.values.administrator;

  //   if (!administratorId) {
  //     return null;
  //   }

  //   const labelFromList = administratorsList.find(
  //     (item) => item._id === administratorId,
  //   )?.fullName;

  //   return {
  //     label: selectedAdministratorLabel || labelFromList || '',
  //     value: administratorId,
  //   };
  // }, [
  //   validation.values.administrator,
  //   selectedAdministratorLabel,
  //   administratorsList,
  // ]);

  // const onAdministratorChange = React.useCallback(
  //   (e) => {
  //     validation.setFieldValue('administrator', e?.value || '', true);
  //     setSelectedAdministratorLabel(e?.label || '');
  //   },
  //   [validation],
  // );

  const buildMarketListPayload = React.useCallback(({ page, append, q }) => {
    const payload = {
      page,
      limit: MARKET_PAGE_LIMIT,
      append,
    };

    if (q) {
      payload.q = q;
    }

    return payload;
  }, []);

  // const buildAdministratorListPayload = React.useCallback(
  //   ({ page, append, q }) => {
  //     const payload = {
  //       page,
  //       limit: ADMINISTRATOR_PAGE_LIMIT,
  //       append,
  //     };

  //     if (q) {
  //       payload.q = q;
  //     }

  //     return payload;
  //   },
  //   [],
  // );

  const debouncedSearchMarkets = React.useMemo(
    () =>
      debounce((searchValue) => {
        setMarketSearchQuery(searchValue);
        setMarketPage(1);
        actionGetMarketList(
          buildMarketListPayload({
            page: 1,
            append: false,
            q: searchValue,
          }),
        );
      }, 400),
    [actionGetMarketList, buildMarketListPayload],
  );

  // const debouncedSearchAdministrators = React.useMemo(
  //   () =>
  //     debounce((searchValue) => {
  //       setAdministratorSearchQuery(searchValue);
  //       setAdministratorPage(1);
  //       actionGetAdministratorsList(
  //         buildAdministratorListPayload({
  //           page: 1,
  //           append: false,
  //           q: searchValue,
  //         }),
  //       );
  //     }, 400),
  //   [actionGetAdministratorsList, buildAdministratorListPayload],
  // );

  React.useEffect(
    () => () => debouncedSearchMarkets.cancel(),
    [debouncedSearchMarkets],
  );

  // React.useEffect(
  //   () => () => debouncedSearchAdministrators.cancel(),
  //   [debouncedSearchAdministrators],
  // );

  const onMarketInputChange = React.useCallback(
    (inputValue, { action }) => {
      if (action === 'input-change') {
        debouncedSearchMarkets(inputValue);
      }
    },
    [debouncedSearchMarkets],
  );

  const resetMarketList = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    actionGetMarketList(
      buildMarketListPayload({ page: 1, append: false, q: '' }),
    );
  }, [actionGetMarketList, buildMarketListPayload, debouncedSearchMarkets]);

  const onMarketMenuOpen = React.useCallback(() => {
    resetMarketList();
  }, [resetMarketList]);

  const onMarketBlur = React.useCallback(() => {
    debouncedSearchMarkets.cancel();
  }, [debouncedSearchMarkets]);

  const onLoadMoreMarkets = React.useCallback(() => {
    if (isLoadingMarketList || !hasMoreMarkets) {
      return;
    }

    const nextPage = marketPage + 1;
    setMarketPage(nextPage);
    actionGetMarketList(
      buildMarketListPayload({
        page: nextPage,
        append: true,
        q: marketSearchQuery,
      }),
    );
  }, [
    actionGetMarketList,
    buildMarketListPayload,
    hasMoreMarkets,
    isLoadingMarketList,
    marketPage,
    marketSearchQuery,
  ]);

  // const onAdministratorInputChange = React.useCallback(
  //   (inputValue, { action }) => {
  //     if (action === 'input-change') {
  //       debouncedSearchAdministrators(inputValue);
  //     }
  //   },
  //   [debouncedSearchAdministrators],
  // );

  // const resetAdministratorList = React.useCallback(() => {
  //   debouncedSearchAdministrators.cancel();
  //   setAdministratorSearchQuery('');
  //   setAdministratorPage(1);
  //   actionGetAdministratorsList(
  //     buildAdministratorListPayload({ page: 1, append: false, q: '' }),
  //   );
  // }, [
  //   actionGetAdministratorsList,
  //   buildAdministratorListPayload,
  //   debouncedSearchAdministrators,
  // ]);

  // const onAdministratorMenuOpen = React.useCallback(() => {
  //   resetAdministratorList();
  // }, [resetAdministratorList]);

  // const onAdministratorBlur = React.useCallback(() => {
  //   debouncedSearchAdministrators.cancel();
  // }, [debouncedSearchAdministrators]);

  // const onLoadMoreAdministrators = React.useCallback(() => {
  //   if (isLoadingGetAdministratorsList || !hasMoreAdministrators) {
  //     return;
  //   }

  //   const nextPage = administratorPage + 1;
  //   setAdministratorPage(nextPage);
  //   actionGetAdministratorsList(
  //     buildAdministratorListPayload({
  //       page: nextPage,
  //       append: true,
  //       q: administratorSearchQuery,
  //     }),
  //   );
  // }, [
  //   actionGetAdministratorsList,
  //   buildAdministratorListPayload,
  //   administratorPage,
  //   administratorSearchQuery,
  //   hasMoreAdministrators,
  //   isLoadingGetAdministratorsList,
  // ]);

  const onResetForm = React.useCallback(() => {
    validation.resetForm();
    setSelectedMarketLabel('');
    // setSelectedAdministratorLabel('');
    debouncedSearchMarkets.cancel();
    // debouncedSearchAdministrators.cancel();
    setMarketSearchQuery('');
    setMarketPage(1);
    // setAdministratorSearchQuery('');
    // setAdministratorPage(1);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    actionClearLandmarkList();
    fetchRevenueList({
      sortField: DEFAULT_SORT_BY,
      sortOrder: DEFAULT_SORT_ORDER,
      page: 1,
      q: '',
      market: '',
      placeType: '',
      landmark: '',
      starRating: '',
      status: '',
      isPublished: '',
      administrator: '',
    });

    if (urlPage !== 1) {
      history.push('?page=1');
    }
  }, [
    // debouncedSearchAdministrators,
    debouncedSearchMarkets,
    actionClearLandmarkList,
    fetchRevenueList,
    history,
    urlPage,
    validation,
  ]);

  const isSearchBtnDisabled = React.useMemo(
    () =>
      validation.values.q === '' &&
      validation.values.market === '' &&
      validation.values.placeType === '' &&
      validation.values.landmark === '' &&
      validation.values.starRating === '' &&
      validation.values.status === '' &&
      validation.values.isPublished === '' &&
      validation.values.administrator === '',
    [validation],
  );

  const onChangePage = React.useCallback(
    (selected) => {
      history.push(`?page=${selected.selected + 1}`);
      // tableRef.current.scrollIntoView({ behavior: 'smooth' });
    },
    [history],
  );

  const pageCount = React.useMemo(
    () => getTotalPage(totalRevenue, DEFAULT_LIMIT),
    [totalRevenue],
  );

  React.useEffect(() => {
    fetchRevenueList({ page: urlPage });
  }, [urlPage, fetchRevenueList]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name="Danh sách doanh thu" />

      <Container fluid>
        {/* Render Breadcrumb */}
        <Breadcrumb
          pageTitle="Quản lý doanh thu"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <form onSubmit={onSearchSubmit}>
            <Row className="Section__Search__Filter">
              <div className="flex-1 flex-shrink-0" style={{ minWidth: 300 }}>
                <Label className="Section__Search__Label">Tìm kiếm</Label>

                <div className="Section__Search__Container">
                  <AppInputValidation
                    name="q"
                    id="q"
                    type="text"
                    placeholder="Tìm theo mã chỗ nghỉ, tên chỗ nghỉ"
                    validation={validation}
                    inputClassName={cn('Section__Search__Input')}
                  />

                  <img
                    src={SearchIcon}
                    alt="search"
                    className="Section__Search__Icon__Search"
                  />
                </div>
              </div>

              <div className="flex-2">
                <Label className="Section__Search__Label">Bộ lọc</Label>

                <div className="Section__Filter__Container">
                  <AppSelectValidation
                    placeholder="Thị trường"
                    name="market"
                    key="market"
                    options={marketOptions}
                    validation={validation}
                    value={marketSelectValue}
                    isUsePureValue
                    onChange={onMarketChange}
                    onInputChange={onMarketInputChange}
                    onBlur={onMarketBlur}
                    onMenuOpen={onMarketMenuOpen}
                    onMenuScrollToBottom={onLoadMoreMarkets}
                    filterOption={() => true}
                    isLoading={isLoadingMarketList}
                    selectedClassName={styles.FilterInput}
                    isHiddenSearchable
                  />

                  <AppSelectValidation
                    placeholder="Khu vực"
                    name="landmark"
                    key={`landmark-${validation.values.market}`}
                    options={landmarkOptions}
                    validation={validation}
                    value={validation.values.landmark}
                    onChange={onChangeFilterOption}
                    selectedClassName={styles.FilterInput}
                    isHiddenSearchable
                    isGroup
                    isDisabled={!validation.values.market}
                    isLoading={isLoadingLandmarkList}
                  />

                  <AppSelectValidation
                    placeholder="Loại hình"
                    name="placeType"
                    key="placeType"
                    options={placeTypeOptions}
                    validation={validation}
                    value={validation.values.placeType}
                    onChange={onChangeFilterOption}
                    selectedClassName={styles.FilterInput}
                    isHiddenSearchable
                  />

                  <AppSelectValidation
                    placeholder="Hạng sao"
                    name="starRating"
                    key="starRating"
                    options={STAR_RATING_OPTIONS}
                    validation={validation}
                    value={validation.values.starRating}
                    onChange={onChangeFilterOption}
                    selectedClassName={styles.FilterInput}
                    isHiddenSearchable
                  />

                  {/* <AppSelectValidation
                      placeholder="Trạng thái"
                      name="status"
                      id="filter-status"
                      key="status"
                      options={STATUS_LIST}
                      validation={validation}
                      value={validation.values.status}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Trạng thái đóng mở"
                      name="isPublished"
                      key="isPublished"
                      options={STATUS_OPEN_CLOSE_OPTIONS}
                      validation={validation}
                      value={validation.values.isPublished}
                      onChange={onChangeFilterOption}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    />

                    <AppSelectValidation
                      placeholder="Người phụ trách"
                      name="administrator"
                      key="administrator"
                      options={administratorOptions}
                      validation={validation}
                      value={administratorSelectValue}
                      isUsePureValue
                      onChange={onAdministratorChange}
                      onInputChange={onAdministratorInputChange}
                      onBlur={onAdministratorBlur}
                      onMenuOpen={onAdministratorMenuOpen}
                      onMenuScrollToBottom={onLoadMoreAdministrators}
                      filterOption={() => true}
                      isLoading={isLoadingGetAdministratorsList}
                      selectedClassName={styles.FilterInput}
                      isHiddenSearchable
                    /> */}
                </div>
              </div>

              <div className="d-flex flex-1 align-self-end">
                <Button
                  outline
                  classes="Section__Search__Button__ReType"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </Row>

          </form>
        </Card>

        <Card className="Section">
          <div className="d-flex Table__Header">
            <Label className="Table__Header__Total">
              {`${totalRevenue} đối tác`}
            </Label>

            <div className="d-flex gap-2">
              <Button
                outline
                classes="Table__Header__Button__ReType"
                color={themeColor.LIGHT}
                disabled={isLoadingExportRevenueList}
                onClick={onExportRevenueList}
              >
                <img src={DownloadIcon} alt="download" />
                <span>Xuất file</span>
              </Button>
            </div>
          </div>

          <div className="Table__Body">
            <DataTableRevenue
              isLoadingRevenueList={isLoadingRevenueList}
              revenues={revenueList}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          </div>
        </Card>

        <Pagination
          page={urlPage}
          pageCount={pageCount}
          onChangePage={onChangePage}
        />
      </Container>
    </div>
  );
}

ManagerRevenue.propTypes = {
  isLoadingRevenueList: PropTypes.bool.isRequired,
  isLoadingExportRevenueList: PropTypes.bool,
  isLoadingMarketList: PropTypes.bool,
  isLoadingLandmarkList: PropTypes.bool,
  // isLoadingGetAdministratorsList: PropTypes.bool,

  revenueList: PropTypes.instanceOf(Array),
  totalRevenue: PropTypes.number,
  marketList: PropTypes.instanceOf(Array),
  marketTotal: PropTypes.number,
  landmarkOptions: PropTypes.instanceOf(Array),
  // administratorsList: PropTypes.instanceOf(Array),
  // administratorsTotal: PropTypes.number,

  actionGetRevenueList: PropTypes.func.isRequired,
  actionExportRevenueList: PropTypes.func.isRequired,
  actionGetMarketList: PropTypes.func.isRequired,
  actionGetLandmarkList: PropTypes.func.isRequired,
  actionClearLandmarkList: PropTypes.func.isRequired,
  // actionGetAdministratorsList: PropTypes.func.isRequired,
};

ManagerRevenue.defaultProps = {
  revenueList: [],
  totalRevenue: 0,
  isLoadingExportRevenueList: false,
  marketList: [],
  marketTotal: 0,
  isLoadingMarketList: false,
  landmarkOptions: [],
  isLoadingLandmarkList: false,
  // administratorsList: [],
  // administratorsTotal: 0,
  // isLoadingGetAdministratorsList: false,
};

export default ManagerRevenue;
